/**
 * MiniNode Component
 * WP-DASH-2: Individual node rendering for mini Sankey visualization
 *
 * Renders a single node (income source, account, or expense category)
 * as an SVG group with rectangle, label, and value.
 */

import { memo } from 'react';
import type { PositionedNode } from './miniSankeyUtils';
import { MINI_FLOW_COLORS, truncate, formatCompactCurrency, NODE_SIZING } from './miniSankeyUtils';

// ============================================================================
// Types
// ============================================================================

export type MiniNodeType = 'inflow' | 'account' | 'outflow';

interface MiniNodeProps {
  node: PositionedNode;
  type: MiniNodeType;
}

// ============================================================================
// Color Configuration
// ============================================================================

const NODE_COLORS: Record<MiniNodeType, { background: string; border: string; text: string }> = {
  inflow: MINI_FLOW_COLORS.inflow,
  account: MINI_FLOW_COLORS.account,
  outflow: MINI_FLOW_COLORS.outflow,
};

// ============================================================================
// Component
// ============================================================================

/**
 * MiniNode - Renders a single node in the mini Sankey diagram
 *
 * For account nodes, displays balance instead of flow value.
 * Uses memoization to prevent unnecessary re-renders.
 */
function MiniNodeComponent({ node, type }: MiniNodeProps) {
  const colors = NODE_COLORS[type];

  // For account nodes, show balance; for income/expense nodes, show value
  const displayValue = type === 'account' && node.balance !== undefined
    ? node.balance
    : node.value;

  // Calculate text positioning
  const textX = 6;
  const labelY = node.height < 36 ? node.height / 2 : node.height / 2 - 6;
  const valueY = node.height < 36 ? node.height / 2 : node.height / 2 + 8;

  // For small nodes, show label and value on same line
  const showSingleLine = node.height < 36;

  return (
    <g
      transform={`translate(${node.x}, ${node.y})`}
      className="mini-node"
      aria-label={`${node.label}: ${formatCompactCurrency(displayValue)}`}
    >
      {/* Node rectangle */}
      <rect
        width={node.width}
        height={node.height}
        rx={NODE_SIZING.borderRadius}
        fill={colors.background}
        stroke={colors.border}
        strokeWidth={1}
      />

      {showSingleLine ? (
        /* Single line layout for small nodes */
        <text
          x={textX}
          y={node.height / 2}
          dominantBaseline="middle"
          className="text-[9px] font-medium"
          fill={colors.text}
        >
          <tspan>{truncate(node.label, 8)}</tspan>
          <tspan dx={4} className="opacity-75">{formatCompactCurrency(displayValue)}</tspan>
        </text>
      ) : (
        /* Two line layout for larger nodes */
        <>
          {/* Label */}
          <text
            x={textX}
            y={labelY}
            dominantBaseline="middle"
            className="text-[10px] font-medium"
            fill={colors.text}
          >
            {truncate(node.label, 10)}
          </text>

          {/* Value */}
          <text
            x={textX}
            y={valueY}
            dominantBaseline="middle"
            className="text-[9px] opacity-75"
            fill={colors.text}
          >
            {formatCompactCurrency(displayValue)}
          </text>
        </>
      )}
    </g>
  );
}

export const MiniNode = memo(MiniNodeComponent);

// ============================================================================
// Mini Node Column Component
// ============================================================================

interface MiniNodeColumnProps {
  nodes: PositionedNode[];
  type: MiniNodeType;
}

/**
 * MiniNodeColumn - Renders a column of nodes
 */
function MiniNodeColumnComponent({ nodes, type }: MiniNodeColumnProps) {
  return (
    <g className={`mini-node-column mini-node-column-${type}`}>
      {nodes.map((node) => (
        <MiniNode key={node.id} node={node} type={type} />
      ))}
    </g>
  );
}

export const MiniNodeColumn = memo(MiniNodeColumnComponent);

// ============================================================================
// More Indicator Component
// ============================================================================

interface MiniMoreIndicatorProps {
  x: number;
  y: number;
  count: number;
  type: MiniNodeType;
}

/**
 * MiniMoreIndicator - Shows "+N more" when nodes exceed display limit
 */
function MiniMoreIndicatorComponent({ x, y, count, type }: MiniMoreIndicatorProps) {
  const colors = NODE_COLORS[type];

  return (
    <text
      x={x}
      y={y}
      className="text-[9px] font-medium"
      fill={colors.text}
      opacity={0.6}
    >
      +{count} more
    </text>
  );
}

export const MiniMoreIndicator = memo(MiniMoreIndicatorComponent);

export default MiniNode;
